import { Link } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carTagline } from '../../domain/cars/carCatalogue';
import { carPrizeShareMessage } from '../../domain/cars/carShare';
import CarGallery from '../cars/CarGallery';
import CarShareSheet from '../cars/CarShareSheet';
import CarSpecPlate from '../cars/CarSpecPlate';
import CarSpecTable from '../cars/CarSpecTable';

/**
 * The card the ceremony ends on — the car, introduced (SPEC-010 PR-10.4).
 *
 * The last act, and the only one with anything to read in it. Everything above
 * it was withholding; this is the pay-off, and the pay-off is the catalogue
 * itself: the four figures a fourteen-year-old could recite, every picture of
 * the car this pack holds, and the full twenty-three-row specification.
 *
 * NOTHING IS RE-DRAWN FOR THE OCCASION. The plate, the gallery and the table are
 * the same three components the car's own detail screen uses, and that is a
 * decision rather than a convenience: a "prize spec sheet" beside the real one
 * would be a second drawing of the same figures, and the first corrected
 * horsepower would make one of them a lie. It also means the reader recognises
 * the card when they meet it again in the garage, which is where it now lives.
 *
 * THIS BAND SCROLLS and the ceremony above it does not. A dialog is the one
 * place in this product where that is the right way round: the duel screen
 * cannot scroll because the answer must never be below the fold, and this
 * screen has already given the reader the answer — everything under it is
 * theirs to read at their own pace, and a specification clipped to a phone's
 * fold would be a specification nobody sees.
 */
export default function CarPrizeCard({
    car,
    closeKey = 'prize.close',
    name,
    nextTarget,
    record,
    onClose,
}) {
    const { localeCode } = useLocale();
    const t = useTranslate();
    const tagline = carTagline(car);

    return (
        <section className="car-prize__card">
            {/* A live region, because the ceremony is not a navigation and none
                of it announces itself. For a reader on a screen reader this
                sentence IS the walkout. */}
            <div className="car-prize__claim" role="status">
                <p className="car-prize__joined">{t('prize.joined')}</p>
                {record !== null ? (
                    <p className="car-prize__record figure">{t('prize.record', { score: record })}</p>
                ) : null}
            </div>

            {tagline ? <p className="car-prize__tagline">{tagline}</p> : null}

            <CarSpecPlate car={car} />

            {/* The two actions, in the order the reader wants them: sharing is
                the thing they have just been given a reason to do, and going
                back is what they were doing before. The share opens the
                catalogue's own chooser carrying the ceremony's words — the
                picture is the card that already exists for every car
                (SPEC-010 D-18), and what changes is the sentence. */}
            <div className="car-prize__actions">
                <CarShareSheet
                    car={car}
                    compose={carPrizeShareMessage}
                    actionKey="prize.share.action"
                />

                {/* WHERE THE READER IS GOING BACK TO, which is not the same
                    place twice since a car can be taken mid-run (SPEC-010
                    D-30). A run that has ended offers the competition; a run
                    that is still live offers the streak it is still on, because
                    that is what the reader is about to walk back into and this
                    button is the last thing they read before they do. A key
                    rather than a label, and parameterised rather than copied for
                    the same reason the share sheet above it is (D-21). */}
                <button type="button" className="button button--quiet car-prize__done" onClick={onClose}>
                    {t(closeKey)}
                </button>
            </div>

            {/* What the NEXT car costs, said once, here, and nowhere else on this
                screen. A ladder quoted in the middle of a celebration would be
                the product asking for the next run before the reader has
                finished the last one; quoted at the foot of it, after the specs
                and the pictures, it is the reason to press play again. */}
            <p className="car-prize__ladder">
                {nextTarget === null ? t('prize.last') : t('prize.next', { score: nextTarget })}
            </p>

            <CarGallery car={car} />

            {/* An h2 like the gallery's above it, and a peer of the ceremony's
                own title rather than a child of it: this dialog has no h1, and
                two bands of the same kind must not be at two levels. */}
            <header className="section-heading">
                <h2 className="section-heading__text">{t('cars.section.specs')}</h2>
                <span className="section-heading__rule" aria-hidden="true" />
            </header>
            <CarSpecTable car={car} />

            {/* The way into the durable version of this moment. The ceremony is
                shown once and never again (SPEC-010 D-13); the car's own screen
                is where it lives from now on, and a reader who wants to look at
                it properly should not have to find it on the shelf. */}
            <Link className="car-prize__link" to={`/${localeCode}/cars/${car.id}`}>
                {t('prize.view', { car: name })}
            </Link>
        </section>
    );
}
